// src/pages/Dashboard.tsx
import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, Tabs, Tab, List, ListItem, ListItemText, Button, Checkbox, Alert, useMediaQuery } from '@mui/material';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { Team, Member } from '../types/Types';
import { useAdmin } from '../contexts/AdminContext';
import { useAuth } from '../contexts/AuthContext';
import { getTeams, getOwners, getTeamsRevealed } from '../firestoreServices';
import Leaderboard from './Leaderboard';
import MyTeam from './MyTeam';
import PoolEntries from './PoolEntries';
import { Teams } from './Teams';
import BettingExchange from '../components/casino/BettingExchange';
import Casino from '../components/casino/Casino';
import BoysTrip from './BoysTrip';
import FantasyFootball from './FantasyFootball';
import MantisDefaultDashboard from '../mantis/pages/dashboard';
import ThemeCustomization from '../mantis/themes';
import { masters, mastersFonts } from '../theme/mastersTheme';
// import MultiplayerCanvas from "../components/MultiplayerCanvas";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState(1);
  const { currentAuthUser, profile, logout } = useAuth();
  const { isAdmin } = useAdmin();

  const [teams, setTeams] = useState<Team[]>([]);
  const [owners, setOwners] = useState<Member[]>([]);
  // const [userTeams, setUserTeams] = useState<Team[]>([]);
  const [hasTeam, setHasTeam] = useState<Boolean>(false);

  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [teamsRevealed, setTeamsRevealed] = useState<boolean>(false);

  const casinoAccess = [""]

  useEffect(() => {
    // console.log(currentAuthUser.uid)
    const fetchTeams = async () => {
      try {
        const result = await getTeams();
        if (!result.success) {
          setError(result.error)
          return;
        }
        const teamsList = result.data;
        teamsList.sort((a, b) => a.total_score - b.total_score);
        setTeams(teamsList);

        // console.log(teamsList)
        const userTeams = teamsList.filter(team => team.owner_id === currentAuthUser.uid);
        // setUserTeams(userTeams);
        if (userTeams.length > 0) {
          setHasTeam(false);
        } else {
          setHasTeam(true);
        }
      } catch (err) {
        // console.error("Error fetching teams:", err);
        setError("Failed to fetch teams.");
      }
    }

    fetchTeams();

    const fetchTeamsRevealed = async () => {
      const revealed = await getTeamsRevealed();
      setTeamsRevealed(revealed);
    };
    fetchTeamsRevealed();
  }, [currentAuthUser]);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const result = await getOwners()
        if (!result.success) {
          setError(result.error)
          return;
        }
        const ownersList = result.data;
        // ownersList.sort((a, b) => a.name.localeCompare(b.name));
        setOwners(ownersList);
      } catch (err) {
        console.error("Error fetching teams:", err);
        setError("Failed to fetch teams.");
      }
    };

    fetchMembers();
  }, [currentAuthUser]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleFeedBack = () => {
    navigate('/feedback');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleTermsAndServices = () => {
    navigate('/termsAndServices');
  };

  const handlePickTeam = () => {
    navigate('/player-selection');
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // useEffect(() => {
  //   if (isMobile)
      // setError("This Website is Not Built For Mobile.  If you must, try zooming all the way out")
  //   else
  //     setError("")
  // }, [isMobile]);


  const ghostBtnSx = {
    color: masters.ivory,
    fontFamily: mastersFonts.body,
    fontSize: '0.72rem',
    fontWeight: 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase' as const,
    px: 1.5,
    py: 0.75,
    minWidth: 0,
    border: 'none',
    borderRadius: 0,
    borderBottom: '1px solid transparent',
    '&:hover': {
      backgroundColor: 'transparent',
      borderBottom: `1px solid ${masters.champion}`,
      color: masters.championBright,
    },
  };

  return (
    <Box
      component="main"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        width: '100%',
        overflowX: 'hidden',
        scrollbarWidth: 'none',
        backgroundColor: masters.mastersGreen,
        backgroundImage: `radial-gradient(ellipse at top, ${masters.augustaGreen} 0%, ${masters.mastersGreen} 55%, ${masters.pineShadow} 100%)`,
        px: { xs: 1.5, sm: 3, md: 5 },
        py: { xs: 1, sm: 2, md: 3 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 0.5,
          width: '100%',
          maxWidth: '1200px',
          mx: 'auto',
          mb: 2,
        }}
      >
        {isAdmin && (
          <Button type="button" onClick={() => navigate('/admin')} sx={ghostBtnSx}>
            Admin
          </Button>
        )}
        <Button type="button" onClick={handleFeedBack} sx={ghostBtnSx}>
          Feedback
        </Button>
        <Button type="button" onClick={handleLogout} sx={ghostBtnSx}>
          Logout
        </Button>
        <Button type="button" onClick={handleTermsAndServices} sx={ghostBtnSx}>
          Terms
        </Button>
      </Box>

      <Box
        sx={{
          width: '100%',
          maxWidth: '1200px',
          mx: 'auto',
          mb: 3,
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2.5, sm: 3, md: 3.5 },
          borderTop: `1px solid ${masters.ivoryLine}`,
          borderBottom: `2px solid ${masters.champion}`,
          backgroundColor: 'rgba(8, 42, 32, 0.55)',
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontFamily: mastersFonts.body,
            fontSize: '0.7rem',
            fontWeight: 600,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: masters.champion,
            mb: 0.75,
          }}
        >
          Masters Pick-em
        </Typography>
        <Typography
          component="h1"
          sx={{
            fontFamily: mastersFonts.display,
            fontWeight: 700,
            fontStyle: 'italic',
            fontSize: { xs: '2.1rem', sm: '2.8rem', md: '3.4rem' },
            lineHeight: 1.05,
            color: masters.ivory,
            mb: 1,
          }}
        >
          Washed Up
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            color: masters.ivoryMuted,
            fontFamily: mastersFonts.body,
            fontSize: '0.75rem',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: masters.pimentoSoft,
                boxShadow: `0 0 0 4px rgba(168, 35, 26, 0.2)`,
                animation: 'mastersPulse 1.8s ease-in-out infinite',
                '@keyframes mastersPulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.4 },
                },
              }}
            />
            Live
          </Box>
          <Box sx={{ color: masters.ivoryDim }}>•</Box>
          <Box>Final Round</Box>
          <Box sx={{ color: masters.ivoryDim }}>•</Box>
          <Box>Augusta National</Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          width: '100%',
          maxWidth: '1200px',
          mx: 'auto',
          mb: 3,
        }}
      >
        {message && <Alert sx={{ mb: 2 }} severity="success">{message}</Alert>}
        {error && <Alert sx={{ mb: 2 }} severity="error">{error}</Alert>}
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          aria-label="dashboard tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: `1px solid ${masters.ivoryLine}`,
            minHeight: 48,
            '& .MuiTabs-flexContainer': {
              justifyContent: { xs: 'flex-start', md: 'center' },
              gap: { xs: 0.5, md: 2 },
            },
            '& .MuiTabs-indicator': {
              height: 3,
              backgroundColor: masters.champion,
            },
            '& .MuiTab-root': {
              minWidth: 'auto',
              minHeight: 48,
              textTransform: 'uppercase',
              fontFamily: mastersFonts.body,
              fontSize: { xs: '0.72rem', sm: '0.78rem' },
              fontWeight: 600,
              letterSpacing: '0.14em',
              color: masters.ivoryMuted,
              px: { xs: 1.5, sm: 2.5 },
              '&:hover': {
                color: masters.ivory,
              },
              '&.Mui-selected': {
                color: masters.ivory,
              },
            },
            '& .MuiTabScrollButton-root': {
              color: masters.ivoryMuted,
            },
          }}
        >
          <Tab label="Fantasy Football" />
          <Tab label="Fantasy Golf" />
          <Tab label="Betting Exchange" />
          <Tab label="Boys Trip" />
        </Tabs>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            backgroundColor: 'rgba(8, 42, 32, 0.35)',
            borderLeft: `1px solid ${masters.ivoryLine}`,
            borderRight: `1px solid ${masters.ivoryLine}`,
            borderBottom: `1px solid ${masters.ivoryLine}`,
            p: { xs: 1.5, sm: 2.5, md: 3 },
            width: '100%',
            mx: 'auto',
          }}
        >
        {tabIndex === 0 && (profile && <FantasyFootball />)}
          {tabIndex === 1 && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: mastersFonts.body,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: masters.champion,
                  }}
                >
                  Leaderboard
                </Typography>
                {hasTeam && (
                  <Button
                    type="button"
                    onClick={handlePickTeam}
                    sx={{
                      fontFamily: mastersFonts.body,
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      color: masters.mastersGreen,
                      backgroundColor: masters.champion,
                      px: 2.5,
                      py: 0.75,
                      borderRadius: 0,
                      '&:hover': {
                        backgroundColor: masters.championBright,
                      },
                    }}
                  >
                    Create A Team
                  </Button>
                )}
              </Box>
              <Leaderboard teams={teams} owners={owners} teamsRevealed={teamsRevealed}></Leaderboard>
            </Box>
          )}
          {tabIndex === 2 && (profile && <BettingExchange owners={owners} />)}
          {tabIndex === 3 && (profile && <BoysTrip />)}
          {/* {tabIndex === 4 && ( */}
          {/*   <Box> */}
          {/*     {casinoAccess.includes(currentAuthUser.uid) ? */}
          {/*       (profile && <Casino />) : */}
          {/*       ( */}
          {/*         <> */}
          {/*           <Typography variant="h6">Casino</Typography> */}
          {/*           <Typography variant="h6">Coming Soon...</Typography> */}
          {/*           <iframe */}
          {/*             width="100%" */}
          {/*             height="700" */}
          {/*             src="https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1" */}
          {/*             frameBorder="0" */}
          {/*             allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" */}
          {/*             allowFullScreen */}
          {/*             title="Embedded Video" */}
          {/*           ></iframe> */}
          {/*         </> */}
          {/*       )} */}
          {/*   </Box> */}
          {/* )} */}
          {/* {tabIndex === 5 && ( */}
          {/*   <Box sx={{ mt: 3 }}> */}
          {/*     <MantisDefaultDashboard /> */}
          {/*   </Box> */}
          {/* )} */}
          {/* {tabIndex === 6 && ( */}
          {/*   <Box sx={{ mt: 3 }}> */}
          {/*     <MultiplayerCanvas /> */}
          {/*   </Box> */}
          {/* )} */}
        </Box>
      </Box>
    </Box>
    // /* </Container> */
  );
};

export default Dashboard;
